// Turns an approval card from the server (made by Claude) into what the Approvals pages show.
// Claude fills the card's `draft` as written down in the vault's "SERB app connection" note.
import { formatJordan, type CardStatus, type CardView } from '@serb/shared';
import type { Tone } from '../../components/tone';
import { KIND_LABEL, type Approval, type ApprovalKind, type ApprovalStatus, type Quote, type Urgency } from './sample';

const TAG: Record<ApprovalKind, [string, Tone]> = {
  quotation: ['Quotation', 'error'],
  'agent-message': ['Agent Message', 'info'],
  booking: ['Booking', 'info'],
  'customer-message': ['Customer Message', 'info'],
  jcs: ['Financial', 'neutral'],
};

const STATUS: Partial<Record<CardStatus, ApprovalStatus>> = {
  pending: 'pending',
  approved: 'approved',
  edited: 'approved',
  sent: 'sent',
  failed: 'failed',
  rejected: 'rejected',
};

/** "2h ago", "3d ago" */
function ago(iso: string, now = Date.now()) {
  const minutes = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
}

const text = (v: unknown) => (typeof v === 'string' ? v : '');

/** The subject of the email this card answers; empty when it will go out as a new email */
function replyIn(d: Record<string, unknown>) {
  const r = d.replyTo;
  return r && typeof r === 'object' ? text((r as Record<string, unknown>).subject) || text(d.subject) : undefined;
}

export function cardToApproval(c: CardView): Approval | null {
  const status = STATUS[c.status];
  if (!status) return null; // replaced or expired cards are not shown
  // what will be sent: Claude's draft, with his changes on top once he edited it
  const d = { ...c.draft, ...(c.final ?? {}) };
  const kind: ApprovalKind = c.kind in KIND_LABEL ? (c.kind as ApprovalKind) : 'agent-message';
  const [tag, tagTone] = TAG[kind];
  const urgency = c.urgent ? 'High' : (['High', 'Medium', 'Normal', 'Low'] as Urgency[]).find((u) => u === d.urgency);
  return {
    id: c.id,
    srb: c.code,
    kind,
    tag,
    tagTone,
    title: c.title,
    subtitle: text(d.subtitle),
    summary: c.summary,
    homeDescription: text(d.homeDescription) || text(d.subtitle) || c.title,
    homeValue: text(d.homeValue),
    age: ago(c.createdAt),
    urgency: urgency ?? 'Normal',
    status,
    created: formatJordan(c.createdAt, { weekday: undefined, year: 'numeric' }),
    requestedBy: 'Claude',
    to: text(d.to) || undefined,
    cc: text(d.cc) || undefined,
    subject: text(d.subject) || undefined,
    files: c.files,
    replyIn: replyIn(d),
    inSpark: c.inDrafts,
    editedInSpark: d.editedIn === 'spark',
    body: text(d.body) || undefined,
    lines: Array.isArray(d.lines) ? (d.lines as [string, number][]) : undefined,
    linesCurrency: text(d.linesCurrency) || undefined,
    quote: d.quote ? (d.quote as Quote) : undefined,
    history: [
      [formatJordan(c.createdAt, { weekday: undefined, day: undefined, month: undefined }), 'Created by Claude'],
      ...(c.decidedAt
        ? ([
            [
              formatJordan(c.decidedAt, { weekday: undefined, day: undefined, month: undefined }),
              c.decisionNote || `You ${status}`,
            ],
          ] as Approval['history'])
        : []),
    ],
    instructions: c.instructions,
  };
}
